import { useMemo } from 'react';

import {
  definedAccessor,
  xAxisAccessor as x,
  yAxisAccessor as y,
} from './accessors.js';
import { LineCanvas } from '../../../../core/components/canvas/components/LineCanvas.js';
import { shouldDisplayPoints } from '../../../../core/components/canvas/utils/display-points.js';
import { useGridPlotSize } from '../../../../core/components/chart-grid/hooks/useGridPlotSize.js';
import { TimeseriesDatapoint } from '../../../../core/types/timeseries.js';
import { isIsolatedPoint } from '../../../../core/utils/is-isolated-point.js';
import { safeGet } from '../../../../core/utils/safe-get.js';
import { LINE_CHART_DISPLAY_ARIA_LABEL } from '../../../constants.js';
import { useSeriesCurve } from '../../../hooks/useSeriesCurve.js';
import { useTimeseriesFilteredData } from '../../../hooks/useTimeseriesFilteredData.js';
import { useYScales } from '../../../hooks/useYScales.js';
import { TimeseriesLineSlotProps } from '../../../types/timeseries.js';
import { BaseDisplayProps } from '../shared/types.js';
import { getClampedTimeScale } from '../shared/utils/clamp-value.js';

export interface LineChartDisplayProps extends BaseDisplayProps {
  series: TimeseriesLineSlotProps[];
}

export const LineChartDisplay = ({ series, xScale }: LineChartDisplayProps) => {
  const yScales = useYScales();
  const { seriesMap, datapointsMap } = useTimeseriesFilteredData();

  const curve = useSeriesCurve();

  const { width, height } = useGridPlotSize();

  const lineSeries = useMemo(() => [...series].reverse(), [series]);

  const isolatedAccessor =
    (displayPoints: boolean) =>
    (datapoints: TimeseriesDatapoint[], index: number) =>
      displayPoints ||
      isIsolatedPoint(datapoints, index, (point) =>
        definedAccessor(point, datapointsMap),
      );

  const clampedScale = getClampedTimeScale(xScale);

  return (
    <figure aria-label={LINE_CHART_DISPLAY_ARIA_LABEL}>
      {lineSeries.map((series) => {
        const seriesMetadata = safeGet(seriesMap, series);

        const displayPoints = shouldDisplayPoints(
          seriesMetadata.pointsDisplay,
          width,
          series.datapoints.length,
        );

        return (
          <LineCanvas
            key={seriesMetadata.id}
            data={series.datapoints}
            canvasWidth={width}
            canvasHeight={height}
            x={x(xScale, datapointsMap, clampedScale)}
            y={y(yScales, datapointsMap, seriesMetadata.unit)}
            defined={(point) => definedAccessor(point, datapointsMap)}
            displayScatterPlot={isolatedAccessor(displayPoints)}
            color={() => seriesMetadata.color.Default}
            opacity={1}
            curve={curve}
            enableDotOverflow
          />
        );
      })}
    </figure>
  );
};

LineChartDisplay['displayName'] = 'LineChartDisplay';
